import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import MDAvatar from "components/MDAvatar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import { useTranslation } from "react-i18next";
import { formatSAR, formatDate } from "../../helper/format";

const ProductDetailsDialog = ({ isOpen, handleClose, fetchProducts }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // The data source resolves from in-memory state; the old setTimeout wrapper
  // forced a 2-second spinner on every open for no reason.
  const fetchProductData = useCallback(async () => {
    setLoading(true);
    try {
      const productsWithDetails = await fetchProducts();
      setProducts(productsWithDetails);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchProducts]);

  useEffect(() => {
    if (isOpen) {
      fetchProductData();
    }
  }, [isOpen]);

  const rows = useMemo(
    () =>
      products.map((product) => {
        return {
          productId: product?._id || t("orders.notAvailable"),
          productName: (
            <div className="max-w-[100px] truncate">
              <p
                className="font-semibold"
                title={`${product.name.en || ""} | ${product.name.ar || ""}`}
              >
                {`${product.name.en || ""} | ${product.name.ar || ""}`}
              </p>
            </div>
          ),
          productimg: (
            <MDAvatar
              src={product.image || "https://via.placeholder.com/150"}
              name={product.name.en}
              size="xl"
            />
          ),
          category: `${product.category.name.en || ""} | ${product.category.name.ar || ""}`,
          description: `${product.description.en || ""} | ${product.description.ar || ""}`,
          price:
            product?.price !== undefined && product?.price !== null
              ? formatSAR(product.price)
              : t("orders.notAvailable"),
          stockQuantity: product?.quantity ?? t("orders.notAvailable"),
          releaseDate: product?.createdAt
            ? formatDate(product.createdAt)
            : t("orders.notAvailable"),
        };
      }),
    [products]
  );

  const columns = [
    { Header: t("orders.productDetails.id"), accessor: "productId", align: "center" },
    { Header: t("orders.productDetails.productImage"), accessor: "productimg", align: "center" },
    { Header: t("orders.productDetails.productName"), accessor: "productName", align: "center" },
    { Header: t("orders.productDetails.description"), accessor: "description", align: "center" },
    { Header: t("orders.productDetails.category"), accessor: "category", align: "center" },
    { Header: t("orders.productDetails.price"), accessor: "price", align: "center" },
    {
      Header: t("orders.productDetails.stockQuantity"),
      accessor: "stockQuantity",
      align: "center",
    },
    { Header: t("orders.productDetails.releaseDate"), accessor: "releaseDate", align: "center" },
  ];

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <DialogTitle>{t("orders.productDetails.title")}</DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <CircularProgress />
          </div>
        ) : (
          <MDBox pt={3}>
            <DataTable
              table={{ columns, rows }}
              isSorted
              entriesPerPage={false}
              showTotalEntries
              noEndBorder
            />
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProductDetailsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  fetchProducts: PropTypes.func.isRequired,
};

export default ProductDetailsDialog;
